import {bubbleStyle} from './bubbles.mjs';
import {focusOpacity} from './pins.mjs';
import {hitSpriteExpression} from './hits.mjs';
import {nearestHit, coarsePointer, touchPointer} from './map-pointer.mjs';
import {installTouchTaps} from './map-taps.mjs';

import {PIN_ZOOM,PIN_FULL_ZOOM,DOT_RADIUS,PIN_GROWTH,DOT_LAYER_IDS} from './map-geometry.mjs';
import {dotOpacity, FOCUS_DIM} from './dot-variants.mjs';
import {GpuDots} from './gpu-dots.mjs';
import {limitGlobeZoom} from './globe-zoom.mjs';
export {PIN_ZOOM,PIN_FULL_ZOOM,DOT_RADIUS,PIN_GROWTH,DOT_LAYER_IDS};
// Match the quantised, dimmed availability fills of the existing pins exactly.
export function dotColor(theme,profile){return ['match',['get','tone'],...Array.from({length:16},(_,i)=>[String(i),bubbleStyle(String(i),theme,profile).fill]).flat(),
  'out',bubbleStyle('out',theme,profile).fill,bubbleStyle('unknown',theme,profile).fill];}
const empty=()=>({type:'FeatureCollection',features:[]});
// The page falls back to the circle layer (?dots=circle) once when the GPU layer cannot draw on this device.
const circleFallback=()=>{try{const url=new URL(location.href);url.searchParams.set('dots','circle');location.replace(url);}catch{/* no page */}};
export function addSiteLayers(map,{theme,profile,focused,gpu=false}) {
  map._w2cFocused=!!focused;
  if(map.getProjection?.()?.type!=='globe')map.setProjection({type:'globe'});
  limitGlobeZoom(map);
  // Dots sit above every map surface (roads, buildings) and below the basemap labels (place names stay
  // readable); pins and hits stay above everything. The dark style starts a label (water_name) before its
  // building and road layers, so anchor on the first label after the last surface layer (Max, 24.10.).
  const styleLayers=map.getStyle?.()?.layers||[];
  let lastSurface=-1;styleLayers.forEach((l,i)=>{if(l.type!=='symbol')lastSurface=i;});
  const labels=styleLayers.slice(lastSurface+1).find(l=>l.type==='symbol')?.id;
  if(map.getSource?.('sites')){
    // Restored after a lost WebGL context: MapLibre re-created the serialisable layers; only the custom layer is missing.
    if(gpu&&map._w2cGpu&&!map.getLayer('site-dots'))map.addLayer(map._w2cGpu,labels);
    return;
  }
  map.addSource('sites',{type:'geojson',data:empty(),cluster:false,maxzoom:13,buffer:16});
  map.addSource('site-pins',{type:'geojson',data:empty(),cluster:false,maxzoom:19,buffer:64});
  if(gpu){
    // The layer object keeps its data across style changes; MapLibre calls onAdd again with the new context.
    const layer=map._w2cGpu??=new GpuDots();layer.onFail??=circleFallback;layer.configure({theme,profile,focused});map.addLayer(layer,labels);
  } else map.addLayer({id:'site-dots',type:'circle',source:'sites',maxzoom:PIN_ZOOM,filter:['!', ['has','rank']],paint:{
    'circle-radius':DOT_RADIUS,'circle-color':dotColor(theme,profile),'circle-opacity':dotOpacity(focused?FOCUS_DIM:1),'circle-stroke-width':0}},labels);
  const layout={'icon-image':['get','pinSprite'],'icon-offset':['get','pinOffsetVector'],'icon-allow-overlap':true,'icon-ignore-placement':true,
    'symbol-sort-key':['get','order'],'symbol-z-order':'source','icon-padding':0};
  map.addLayer({id:'points',type:'symbol',source:'site-pins',minzoom:PIN_ZOOM,filter:['!', ['has','rank']],layout:{...layout,'icon-size':PIN_GROWTH},paint:{
    'icon-opacity':focusOpacity(focused)}});
  map.addLayer({id:'hit-points',type:'symbol',source:'site-pins',filter:['has','rank'],layout,paint:{'icon-opacity':1}});
  map.addLayer({id:'hit-labels',type:'symbol',source:'site-pins',filter:['has','rank'],layout:{
    'icon-image':hitSpriteExpression(theme),'icon-offset':[-29,-31],'icon-allow-overlap':true,'icon-ignore-placement':true,
    'symbol-sort-key':['get','order'],'symbol-z-order':'source'}});
  // Invisible copies that take part in placement (pins themselves ignore it); placePoiYield moves them above the POIs.
  const yieldTo=(id,minzoom,filter,extra)=>map.addLayer({id:`${id}-yield`,type:'symbol',source:'site-pins',minzoom:Math.max(minzoom,POI_MINZOOM),
    filter,layout:{...layout,...extra,'icon-ignore-placement':false},paint:{'icon-opacity':0}});
  yieldTo('points',PIN_ZOOM,['!', ['has','rank']],{'icon-size':PIN_GROWTH});
  yieldTo('hit-points',0,['has','rank'],{});
  yieldTo('hit-labels',0,['has','rank'],{'icon-image':hitSpriteExpression(theme),'icon-offset':[-29,-31]});
  placePoiYield(map);
}
// Basemap POIs yield to pins and hit labels (Max, 01.11.): MapLibre places symbol layers top-down, so the invisible
// copies sit right above the POI layers (tools/protomaps_style.mjs puts them below every other label). Place and road
// labels are placed first and keep their room; a POI under a pin is hidden.
export const POI_LAYERS=['world-pois','pois'],POI_YIELD=['points-yield','hit-points-yield','hit-labels-yield'],POI_MINZOOM=13;
export function placePoiYield(map){
  const layers=map.getStyle?.()?.layers||[],last=Math.max(...POI_LAYERS.map(id=>layers.findIndex(l=>l.id===id)));
  if(last<0||!map.moveLayer)return;
  const before=layers.slice(last+1).find(l=>!POI_YIELD.includes(l.id))?.id;
  for(const id of POI_YIELD)if(map.getLayer(id))map.moveLayer(id,before);
}
// Pins, hit pins and hit labels with their invisible copies: shown only over a ready basemap (app.mjs).
export const PIN_LAYERS=['points','hit-points','hit-labels',...POI_YIELD],HIT_LAYERS=['hit-points','hit-labels'];
// The open site's hit label gives way to its selected marker.
export function syncHitFilter(map,detailId){
  for(const id of ['hit-labels','hit-labels-yield'])if(map.getLayer(id))map.setFilter(id,['all',['has','rank'],['!=',['get','id'],detailId||'']]);
}
export function syncSitePalette(map,theme,profile){map._w2cGpu?.setTheme(theme,profile);for(const id of map._w2cGpu?[]:DOT_LAYER_IDS)if(map.getLayer(id))map.setPaintProperty(id,'circle-color',dotColor(theme,profile));}
// The selected site is drawn by its DOM marker (selected-site.mjs); its map pin and hit pin are hidden by id so no second
// copy (the small one's badge) shows around it. A paint change takes effect at once and keeps placement and fading untouched.
const hideSelected=(base,id)=>id?['case',['==',['get','id'],id],0,base]:base;
export function syncSelectedPin(map,id){
  map._w2cSelectedPin=id||null;
  if(map.getLayer('points'))map.setPaintProperty('points','icon-opacity',hideSelected(focusOpacity(map._w2cFocused),id));
  if(map.getLayer('hit-points'))map.setPaintProperty('hit-points','icon-opacity',map._w2cPinsShown===false?0:hideSelected(1,id));
}
// Pins show only over a ready basemap. The ranked hit layers stay laid out the whole time and are hidden by opacity: switching their
// visibility would send every hit symbol through placement again, so the hits would trail the search pin by a few frames.
export function syncPinVisibility(map,shown){
  map._w2cPinsShown=shown;
  for(const id of PIN_LAYERS)if(map.getLayer(id)&&!HIT_LAYERS.includes(id))map.setLayoutProperty(id,'visibility',shown?'visible':'none');
  for(const id of HIT_LAYERS)if(map.getLayer(id))map.setLayoutProperty(id,'visibility','visible');
  syncSelectedPin(map,map._w2cSelectedPin);
  if(map.getLayer('hit-labels'))map.setPaintProperty('hit-labels','icon-opacity',shown?1:0);
}
export function syncSiteFocus(map,focused){
  map._w2cFocused=focused;map._w2cGpu?.setFocus(focused);
  for(const id of map._w2cGpu?[]:DOT_LAYER_IDS)if(map.getLayer(id))map.setPaintProperty(id,'circle-opacity',dotOpacity(focused?FOCUS_DIM:1));
  if(map.getLayer('points'))map.setPaintProperty('points','icon-opacity',hideSelected(focusOpacity(focused),map._w2cSelectedPin));
}
export function installSiteClick(map,{closeSheet,selected,details,backdrop}) {
  if(map._w2cClick)return;map._w2cClick=true;
  const select=hit=>{
    if(!hit || selected()===hit.properties.id)closeSheet();
    else details(hit.properties.id,true,false,hit.geometry.coordinates);
  };
  const taps=installTouchTaps(map,point=>select(nearestHit(map,point,true)),{backdrop,backdropActive:()=>!!selected()});
  map.on('click',event=>{
    // Ignore compatibility clicks from a touch, even on hybrid laptops.
    if(touchPointer(event.originalEvent, map.getCanvas()))return;
    taps.cancel();
    select(nearestHit(map, event.point, coarsePointer(event.originalEvent, map.getCanvas())));
  });
}
